/**
 * Chartwork object graph store (slice A of the manual and assisted plotting
 * spec). Holds the PlotObject graph keyed by id, in insertion order, and keeps
 * the `children` arrays consistent from each object's `parents` on every
 * mutation. So a builder only has to set parents correctly; the reverse links
 * are derived here and never drift.
 *
 * It also keeps a bounded UNDO history: every mutation snapshots the previous
 * graph first, so a single undo backs out the last plotting action (a whole fix
 * construction, a DR, a removed mark) in one step. References are shared, not
 * deep-copied, because every action replaces byId/order immutably.
 */
import { create } from 'zustand';
import type { PlotObject, PlotObjectId } from '@/chartwork/objects';

const HISTORY_DEPTH = 50;

interface Snapshot {
  byId: Record<PlotObjectId, PlotObject>;
  order: PlotObjectId[];
}

interface ObjectsState {
  byId: Record<PlotObjectId, PlotObject>;
  /** Insertion order, so the renderer iterates deterministically. */
  order: PlotObjectId[];
  /** Past graph states, oldest first, capped at HISTORY_DEPTH. */
  past: Snapshot[];

  /** Add or replace one object. Children links are recomputed. */
  putObject: (object: PlotObject) => void;
  /** Add or replace many at once (e.g. a whole fix construction). */
  putObjects: (objects: PlotObject[]) => void;
  /** Remove an object and prune it from any parents/children lists. */
  removeObject: (id: PlotObjectId) => void;
  clear: () => void;
  /** Undo the last mutation; no-op when there's nothing to undo. */
  undo: () => void;
  canUndo: () => boolean;
}

let nextId = 1;
/** Deterministic-ish id factory for objects created at runtime. */
export function nextObjectId(prefix = 'o'): string {
  return `${prefix}-${nextId++}`;
}

/** The current state pushed onto the history stack, capped to the last N. */
function pushPast(s: { past: Snapshot[]; byId: Snapshot['byId']; order: PlotObjectId[] }): Snapshot[] {
  return [...s.past, { byId: s.byId, order: s.order }].slice(-HISTORY_DEPTH);
}

export const useObjectsStore = create<ObjectsState>((set, get) => ({
  byId: {},
  order: [],
  past: [],

  putObject: (object) =>
    set((s) => {
      const byId = { ...s.byId, [object.id]: object };
      const order = s.order.includes(object.id) ? s.order : [...s.order, object.id];
      return { byId: recomputeChildren(byId), order, past: pushPast(s) };
    }),

  putObjects: (objects) =>
    set((s) => {
      if (objects.length === 0) return s;
      const byId = { ...s.byId };
      const order = [...s.order];
      for (const o of objects) {
        byId[o.id] = o;
        if (!order.includes(o.id)) order.push(o.id);
      }
      return { byId: recomputeChildren(byId), order, past: pushPast(s) };
    }),

  removeObject: (id) =>
    set((s) => {
      if (!s.byId[id]) return s;
      const byId = { ...s.byId };
      delete byId[id];
      // Drop any references to the removed object so the graph stays valid.
      for (const key of Object.keys(byId)) {
        const obj = byId[key];
        if (obj.parents.includes(id)) {
          byId[key] = { ...obj, parents: obj.parents.filter((p) => p !== id) };
        }
      }
      const order = s.order.filter((o) => o !== id);
      return { byId: recomputeChildren(byId), order, past: pushPast(s) };
    }),

  clear: () =>
    set((s) => (s.order.length === 0 ? s : { byId: {}, order: [], past: pushPast(s) })),

  undo: () =>
    set((s) => {
      if (s.past.length === 0) return s;
      const prev = s.past[s.past.length - 1];
      return { byId: prev.byId, order: prev.order, past: s.past.slice(0, -1) };
    }),

  canUndo: () => get().past.length > 0,
}));

/**
 * Rebuild every object's `children` from the `parents` of all objects, so the
 * reverse links are always exactly consistent with the forward ones.
 */
function recomputeChildren(
  byId: Record<PlotObjectId, PlotObject>,
): Record<PlotObjectId, PlotObject> {
  const childrenOf: Record<PlotObjectId, PlotObjectId[]> = {};
  for (const id of Object.keys(byId)) childrenOf[id] = [];
  for (const id of Object.keys(byId)) {
    for (const parent of byId[id].parents) {
      if (childrenOf[parent]) childrenOf[parent].push(id);
    }
  }
  const out: Record<PlotObjectId, PlotObject> = {};
  for (const id of Object.keys(byId)) {
    out[id] = { ...byId[id], children: childrenOf[id] };
  }
  return out;
}
